import * as React from 'react';

import { Activity } from '../../../../../interfaces/activity';
import { UnitTemplateCategoryInterface, UnitTemplateInterface } from '../../../../../interfaces/unitTemplate';
import {
  DataTable,
  Tooltip,
  uniqueValuesArray,
  CONNECT_CAPITALIZED,
  DIAGNOSTIC_CAPITALIZED,
  GRAMMAR_CAPITALIZED,
  LESSONS_CAPITALIZED,
  PROOFREADER_CAPITALIZED,
  READING_FOR_EVIDENCE,
  READING_TEXTS
} from '../../../../Shared';
import { renderActivityPackTooltipElement } from '../../../helpers/unitTemplates';

export const UnitTemplateMinisTable = ({ unitTemplates, userSignedIn }) => {
  const toolColors = {
    [READING_FOR_EVIDENCE]: '#2C7F9B', // $quill-teal
    [CONNECT_CAPITALIZED]: '#935B00', // $quill-gold-dark
    [DIAGNOSTIC_CAPITALIZED]: '#D3443D', // $quill-red
    [GRAMMAR_CAPITALIZED]: '#9035d6', // $quill-purple
    [PROOFREADER_CAPITALIZED]: '#3576C5', // $quill-blue
    [LESSONS_CAPITALIZED]: '#ad277b' // $quill-violet
  }
  const dataTableFields = [
    { name: "Pack type", attribute:"packType", width: "160px", rowSectionClassName: 'pack-type-section', noTooltip: true, isSortable: true, sortAttribute: 'alphabeticalPackType' },
    { name: "Pack name", attribute:"packName", width: "204px", rowSectionClassName: 'pack-name-section', noTooltip: false, isSortable: true, sortAttribute: 'alphabeticalPackName' },
    { name: "Tools", attribute:"tools", width: "140px", rowSectionClassName: '', noTooltip: true, isSortable: true, sortAttribute: 'alphabeticalTools' },
    { name: "Grade Level", attribute:"gradeLevel", width: "70px", rowSectionClassName: '', noTooltip: true, isSortable: true, sortAttribute: 'chronologicalGradeLevel' },
    { name: "Activities", attribute:"activities", width: "50px", rowSectionClassName: 'activities-section', noTooltip: true, isSortable: true },
    { name: "Duration", attribute:"duration", width: "150px", rowSectionClassName: '', noTooltip: true, isSortable: true, sortAttribute: 'chronologicalDuration' },
  ];

  function getDurationElement(unit_template_category: UnitTemplateCategoryInterface, time: string) {
    const { name } = unit_template_category
    if(name === READING_TEXTS) {
      return(
        <section className="duration-section">
          <p>{`${time} mins`}</p>
          <section className="tags-section">
            <p className="duration-beta-tag">BETA</p>
            <p className="gold-new-tag">NEW</p>
          </section>
        </section>
      )
    }
    return `${time} mins`;
  }

  function activityPackTools(activities) {
    if(!activities.length) { return [] }

    return uniqueValuesArray(activities.map((activity: Activity)=> {
      const { classification } = activity
      const { name } = classification
      return name.replace('Quill ', '')
    })).sort();
  }

  function getToolsElement(activities: Activity[]) {
    if(!activities) { return }

    const tools = activityPackTools(activities)

    return(
      <section className="tools-section">
        {tools.map((tool, i) => {
          const toolName = i === tools.length - 1 ? tool : `${tool},`;
          return <p key={toolName} style={{ color: toolColors[tool] }}>{toolName}</p>
        })}
      </section>
    )
  }

  function getNameElement(name: string, activities: Activity[], packUrl:string) {
    const tooltipTriggerElement = <a className="focus-on-light" href={packUrl}>{name}</a>
    return(
      <Tooltip
        tooltipMaxWidth={796}
        tooltipText={renderActivityPackTooltipElement({ activities })}
        tooltipTriggerText={tooltipTriggerElement}
        tooltipTriggerTextClass="activity-pack-name"
      />
    )
  }

  function getChronologicalGradeLevel(gradeBandRange: string) {
    if(!gradeBandRange) { return 0 }
    if(gradeBandRange.slice(0,2) === '10') { return 10 }
    return parseInt(gradeBandRange.slice(0, 1))
  }

  function unitTemplateRows() {
    return unitTemplates.map((unitTemplate: UnitTemplateInterface) => {
      const { id, name, grade_band_range, activities, time, unit_template_category } = unitTemplate;
      const { primary_color } = unit_template_category;
      const packUrl = userSignedIn ? `/assign/featured-activity-packs/${id}` : `/activities/packs/${id}`;
      const nameElement = getNameElement(name, activities, packUrl);
      const durationElement = getDurationElement(unit_template_category, time);
      const toolsElement = getToolsElement(activities);
      return {
        id,
        alphabeticalPackType: unit_template_category.name,
        packType: <p style={{ color: primary_color }}>{unit_template_category.name}</p>,
        alphabeticalPackName: name,
        packName: nameElement,
        tools: toolsElement,
        alphabeticalTools: activityPackTools(activities)[0],
        gradeLevel: grade_band_range,
        chronologicalGradeLevel: getChronologicalGradeLevel(grade_band_range),
        activities: activities.length,
        duration: durationElement,
        chronologicalDuration: time
      }
    });
  }

  return(
    <DataTable
      className="unit-templates-table"
      headers={dataTableFields}
      rows={unitTemplates && unitTemplateRows()}
    />
  )
}

export default UnitTemplateMinisTable;
